import { combineReducers, type PayloadAction } from '@reduxjs/toolkit';

import { resetVideo } from './actions/resetVideo';
import { updateVideo } from './actions/updateVideo';
import { selectChangeSettingsDisabled } from './selectors/selectChangeSettingsDisabled';
import { selectPlayerConfig } from './selectors/selectPlayerConfig';
import { selectPlayerHardReloadDeps } from './selectors/selectPlayerHardReloadDeps';
import { selectPlayerSoftReloadDeps } from './selectors/selectPlayerSoftReloadDeps';
import { updateFile, videoFileInitialState, videoFileReducer, type VideoFileState } from './slices/fileSlice';
import {
  setSettings,
  updateSetting,
  videoSettingsInitialState,
  videoSettingsReducer,
  VideoSettingsState,
} from './slices/settingsSlice';
import {
  setCountries,
  setTTL,
  videoSetupReducer,
  setSetup,
  setName,
  videoSetupInitialState,
  VideoSetupState,
} from './slices/setupSlice';
import {
  setEmbedCode,
  videoEmbedCodeInitialState,
  videoEmbedCodeReducer,
  VideoEmbedCodeState,
} from './slices/videoEmbedCodeState';

const combined = combineReducers({
  file: videoFileReducer,
  settings: videoSettingsReducer,
  setup: videoSetupReducer,
  embedCode: videoEmbedCodeReducer,
});

export interface VideoState {
  file: VideoFileState;
  settings: VideoSettingsState;
  setup: VideoSetupState;
  embedCode: VideoEmbedCodeState;
}

export const videoInitialState = {
  file: videoFileInitialState,
  settings: videoSettingsInitialState,
  setup: videoSetupInitialState,
  embedCode: videoEmbedCodeInitialState,
};

export const videoReducer = (state: typeof videoInitialState | undefined, action: PayloadAction<any>) => {
  if (action.type === resetVideo.type) {
    return videoInitialState;
  }

  if (action.type === updateVideo.type) {
    return { ...state, ...action.payload };
  }

  return combined(state, action);
};

export const videoActions = {
  setTTL,
  setCountries,
  setEmbedCode,
  updateFile,
  resetVideo,
  updateVideo,
  updateSetting,
  setSettings,
  setSetup,
  setName,
};

export const videoSelectors = {
  selectChangeSettingsDisabled,
  selectPlayerConfig,
  selectPlayerSoftReloadDeps,
  selectPlayerHardReloadDeps,
};

export { type SettingsPaths } from './slices/settingsSlice';
